<script setup lang="ts">
// Static illustration for the "Community" landing section, a slice of Ava's
// page with the real widgets: achievement badges next to her name, the friends
// section (FriendsDisplay) and the comments widget floating over the page
// background (CommentsWidget + CommentForm + CommentItem).
withDefaults(defineProps<{
  /** Drop the outer framed panel, for use inside a card that already frames it */
  bare?: boolean
}>(), { bare: false })

const friends = ['mia', 'kai', 'wren', 'arlo']

const comments = [
  { name: 'mia', time: '2h ago', text: 'the rain effect on this page is unreal' },
  { name: 'kai', time: '1d ago', text: 'came from the stream, staying for the playlist' }
]
</script>

<template>
  <div
    aria-hidden="true"
    class="flex w-full select-none flex-col pointer-events-none"
    :class="bare ? '' : 'rounded-2xl border border-default bg-elevated/30 p-5 sm:p-6 shadow-(--surface-shadow)'"
  >
    <MockPage
      bg-position="30% 50%"
      class="flex flex-1 flex-col justify-center rounded-xl p-4 font-space-grotesk sm:p-5"
    >
      <!-- Profile card: name with badges, then the friends section -->
      <div class="mock-card mx-auto w-full max-w-sm px-6 pb-6 pt-5 [zoom:0.85]">
        <MockProfile
          :full="false"
          :avatar-size="64"
        />
        <div class="mt-5">
          <MockSectionTitle
            label="My friends"
            :count="friends.length"
          />
          <div class="mt-4 flex flex-wrap justify-center gap-4">
            <div
              v-for="friend in friends"
              :key="friend"
              class="flex flex-col items-center gap-1.5"
            >
              <span class="relative size-10 overflow-hidden rounded-full ring-1 ring-(--mock-card-border)">
                <MockPhoto
                  :name="`avatar-${friend}`"
                  single
                />
              </span>
              <span class="max-w-14 truncate text-[10px] leading-tight text-(--mock-text-muted)">{{ friend }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Comments widget, as it floats in a page corner -->
      <div class="mt-5 max-w-72">
        <div class="mb-3 flex items-center gap-1.5">
          <UIcon
            name="i-lucide-message-circle"
            class="size-3 text-(--mock-text-dim)"
          />
          <span class="text-[11px] font-medium uppercase tracking-wider text-(--mock-text-muted)">Comments</span>
          <span class="text-[10px] text-(--mock-text-dim)">{{ comments.length }}</span>
        </div>
        <div class="mb-5 flex items-stretch gap-2">
          <div class="flex flex-1 items-center rounded-md bg-(--mock-glass) px-2.5 py-1.5 ring ring-(--mock-glass-border)">
            <span class="text-xs text-(--mock-text-dim)">Write a comment...</span>
          </div>
          <span class="flex items-center rounded-md bg-primary-500 px-2 text-white">
            <UIcon
              name="i-lucide-send"
              class="size-3.5"
            />
          </span>
        </div>
        <div class="space-y-3">
          <div
            v-for="comment in comments"
            :key="comment.name"
            class="flex gap-3"
          >
            <span class="relative size-8 shrink-0 overflow-hidden rounded-full ring-1 ring-(--mock-card-border)">
              <MockPhoto
                :name="`avatar-${comment.name}`"
                single
              />
            </span>
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-2">
                <span class="truncate text-xs font-medium text-(--mock-text)">{{ comment.name }}</span>
                <span class="shrink-0 text-[10px] text-(--mock-text-dim)">{{ comment.time }}</span>
              </div>
              <p class="mt-0.5 text-sm text-(--mock-text-strong)">
                {{ comment.text }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </MockPage>
  </div>
</template>
